import { useState } from 'react';
import inventoryService from '../../../services/inventoryService';
import { usePaginationState } from '../../../hooks/usePaginationState.js';
import { usePaginatedData } from '../../../hooks/usePaginatedData.js';

export const useSuppliers = (options = {}) => {
    const [actionLoading, setActionLoading] = useState(false);
    
    // Use pagination hook for data management
    const paginationState = usePaginationState({
        defaultSort: 'name',
        defaultOrder: 'asc',
        defaultLimit: options.defaultLimit || 10,
        limitOptions: options.limitOptions || [10, 20, 50, 100]
    });

    const { data, pagination, loading, error, setError, refetch } = usePaginatedData(
        inventoryService.getSuppliers,
        paginationState
    );

    const loadSuppliers = async (params = {}) => {
        await refetch(params);
    };

    const createSupplier = async (supplierData) => {
        try {
            setActionLoading(true);
            await inventoryService.createSupplier(supplierData);
            await refetch();
            return { success: true };
        } catch (err) {
            const errorMessage = err.error || err.message || 'Failed to create supplier';
            setError(errorMessage);
            // Don't refetch on error to avoid clearing the error message
            return { success: false, error: errorMessage };
        } finally {
            setActionLoading(false);
        }
    };

    const updateSupplier = async (id, supplierData) => {
        try {
            setActionLoading(true);
            await inventoryService.updateSupplier(id, supplierData);
            await refetch();
            return { success: true };
        } catch (err) {
            const errorMessage = err.error || err.message || 'Failed to update supplier';
            setError(errorMessage);
            // Don't refetch on error to avoid clearing the error message
            return { success: false, error: errorMessage };
        } finally {
            setActionLoading(false);
        }
    };

    const deleteSupplier = async (id) => {
        // Confirmation is now handled by the UI component
        try {
            setActionLoading(true);
            await inventoryService.deleteSupplier(id);
            await refetch();
            return { success: true };
        } catch (err) {
            const errorMessage = err.error || err.message || 'Failed to delete supplier';
            setError(errorMessage);
            // Don't refetch on error to avoid clearing the error message
            return { success: false, error: errorMessage };
        } finally {
            setActionLoading(false);
        }
    };

    // Data is automatically loaded by usePaginatedData hook

    return {
        data,
        suppliers: data,
        pagination,
        loading,
        error,
        setError,
        actionLoading,
        loadSuppliers,
        createSupplier,
        updateSupplier,
        deleteSupplier,
        refetch,
        ...paginationState
    };
};